// Transporte HTTP de la API: cabeceras de autenticación, parseo de errores `{error, detalle, regla}`
// y desvío a los datos simulados cuando `usaMock` es verdadero.
import { config, usaMock } from '../config';
import type { ErrorApi } from './tipos';

export class ApiError extends Error {
  readonly status: number;
  readonly codigo: string;
  readonly detalle: string;
  readonly regla: string | null;

  constructor(status: number, cuerpo: Partial<ErrorApi> | null, textoAlterno?: string) {
    const detalle = cuerpo?.detalle || textoAlterno || mensajePorEstado(status);
    super(detalle);
    this.name = 'ApiError';
    this.status = status;
    this.codigo = cuerpo?.error || `http_${status}`;
    this.detalle = detalle;
    this.regla = cuerpo?.regla ?? null;
  }
}

function mensajePorEstado(status: number): string {
  switch (status) {
    case 0:
      return 'No fue posible conectar con el servidor. Revise su conexión e intente de nuevo.';
    case 400:
      return 'La solicitud tiene datos inválidos.';
    case 401:
      return 'Su sesión expiró. Inicie sesión de nuevo.';
    case 403:
      return 'Su rol no tiene permiso para esta acción.';
    case 404:
      return 'El recurso no existe o fue retirado.';
    case 409:
      return 'Otra persona modificó este registro. Recargue y vuelva a intentarlo.';
    case 422:
      return 'Una regla de negocio bloquea esta acción.';
    default:
      return status >= 500 ? 'El servidor presentó un error. Intente de nuevo en unos minutos.' : `Error ${status}.`;
  }
}

/** Proveedor de cabeceras de autenticación (lo registra el AuthProvider activo). */
type ProveedorCabeceras = () => Promise<Record<string, string>>;
let proveedorCabeceras: ProveedorCabeceras = async () => ({});
let alNoAutorizado: () => void = () => undefined;

export function configurarAutenticacion(opts: { cabeceras: ProveedorCabeceras; noAutorizado: () => void }) {
  proveedorCabeceras = opts.cabeceras;
  alNoAutorizado = opts.noAutorizado;
}

export interface RespuestaCruda {
  status: number;
  cuerpo: unknown;
  blob?: Blob;
}

export interface Peticion {
  metodo: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  ruta: string;
  query?: Record<string, string | number | undefined | null>;
  cuerpo?: unknown;
  binario?: boolean;
}

function construirQuery(q?: Peticion['query']): string {
  if (!q) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

async function transportar(p: Peticion, cabeceras: Record<string, string>): Promise<RespuestaCruda> {
  const rutaCompleta = `${p.ruta}${construirQuery(p.query)}`;
  // Condición literal para que el bundle de producción elimine el módulo simulado.
  if (import.meta.env.VITE_AUTH_MODE === 'dev' && !import.meta.env.VITE_API_URL && usaMock) {
    const { manejarMock } = await import('../mock/servidor');
    return manejarMock(p.metodo, rutaCompleta, p.cuerpo, cabeceras);
  }
  const esForm = typeof FormData !== 'undefined' && p.cuerpo instanceof FormData;
  let r: Response;
  try {
    r = await fetch(`${config.apiUrl}${rutaCompleta}`, {
      method: p.metodo,
      headers: {
        Accept: 'application/json',
        ...(p.cuerpo !== undefined && !esForm ? { 'Content-Type': 'application/json' } : {}),
        ...cabeceras,
      },
      body: p.cuerpo === undefined ? undefined : esForm ? (p.cuerpo as FormData) : JSON.stringify(p.cuerpo),
    });
  } catch {
    return { status: 0, cuerpo: null };
  }
  if (p.binario && r.ok) return { status: r.status, cuerpo: null, blob: await r.blob() };
  const texto = await r.text();
  let cuerpo: unknown = null;
  if (texto) {
    try {
      cuerpo = JSON.parse(texto);
    } catch {
      cuerpo = { detalle: texto.slice(0, 300) };
    }
  }
  return { status: r.status, cuerpo };
}

export async function solicitar<T>(p: Peticion): Promise<T> {
  const cabeceras = await proveedorCabeceras();
  const r = await transportar(p, cabeceras);
  if (r.status >= 200 && r.status < 300) {
    return (p.binario ? r.blob : r.cuerpo) as T;
  }
  const err = new ApiError(r.status, (r.cuerpo as Partial<ErrorApi> | null) ?? null);
  if (r.status === 401) alNoAutorizado();
  throw err;
}

export const esApiError = (e: unknown): e is ApiError => e instanceof ApiError;
